import { useEffect, useMemo, useState, type MouseEvent } from "react";
import { useNavigate } from "react-router";

import { Switch } from "nazare-ui/components";
import { useStoredAuth } from "nazare-ui/utils";

import {
  useEnqueueWorkflow,
  useWorkflowEmailSearch,
  type WorkflowDefinition,
  type WorkflowInputField,
} from "../../../workflows-api";
import { PlatformModal } from "../../../platform-modal";
import { activeWorkflowInputFields, buildWorkflowInput, initialWorkflowInputValues, orderedWorkflowInputFields } from "../../../workflow-input-fields";
import styles from "./workflow-run-modal.module.css";

export { PlatformModal as WorkflowModal } from "../../../platform-modal";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(fields: readonly WorkflowInputField[], values: Record<string, unknown>) {
  const errors: Record<string, string> = {};
  for (const field of activeWorkflowInputFields(fields, values)) {
    if (field.widget === "oneOf" || field.type === "boolean") continue;
    const value = values[field.key];
    if (field.type === "stringArray") {
      const list = Array.isArray(value) ? value.filter((item): item is string => typeof item === "string" && !!item.trim()) : [];
      if (field.required && !list.length) errors[field.key] = `${field.label} is required`;
      else if (field.widget === "multiUserEmail" && list.some((email) => !EMAIL_RE.test(email))) errors[field.key] = "Enter valid email addresses";
    } else {
      const text = typeof value === "string" ? value.trim() : "";
      if (field.required && !text) errors[field.key] = `${field.label} is required`;
      else if (field.widget === "userEmail" && text && !EMAIL_RE.test(text)) errors[field.key] = "Enter a valid email address";
    }
  }
  return errors;
}

function EmailSearchField({ field, value, disabled, onChange }: {
  field: WorkflowInputField;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);
  const results = useWorkflowEmailSearch(debounced);
  return <div className={styles.searchField}>
    <input className={styles.input} value={query || value} disabled={disabled} placeholder={field.placeholder ?? "Search an email…"} onChange={(event) => { setQuery(event.target.value); if (!event.target.value) onChange(""); }} />
    {debounced && <div className={styles.searchResults}>
      {results.isFetching ? <span className={styles.searchMessage}>Searching…</span> : results.error ? <span className={styles.error}>Search failed.</span> : results.data?.results.length ? results.data.results.map((result) => {
        const nextValue = field.valueKey === "classificationId" ? result.classificationId ?? result.email : result.email;
        return <button key={`${result.classificationId ?? ""}:${result.email}`} type="button" className={styles.searchOption} onClick={() => { onChange(nextValue); setQuery(""); }}><strong>{result.subject || result.email}</strong><span>{[result.companyName, result.date, result.email].filter(Boolean).join(" · ")}</span></button>;
      }) : <span className={styles.searchMessage}>No matches.</span>}
    </div>}
  </div>;
}

function FieldControl({ field, values, errors, disabled, onChange }: {
  field: WorkflowInputField;
  values: Record<string, unknown>;
  errors: Record<string, string>;
  disabled: boolean;
  onChange: (key: string, value: unknown) => void;
}) {
  if (field.widget === "oneOf") {
    const selected = typeof values[field.key] === "string" ? values[field.key] as string : field.branches?.[0]?.key ?? "";
    const branch = field.branches?.find((item) => item.key === selected) ?? field.branches?.[0];
    return <div className={styles.branchField}><span className={styles.label}>{field.label}</span><div className={styles.branchTabs}>{(field.branches ?? []).map((item) => <button key={item.key} type="button" className={item.key === selected ? styles.branchActive : styles.branchButton} onClick={() => onChange(field.key, item.key)}>{item.label}</button>)}</div>{branch && orderedWorkflowInputFields(branch.fields).map((child) => <FieldControl key={child.key} field={child} values={values} errors={errors} disabled={disabled} onChange={onChange} />)}</div>;
  }
  const value = values[field.key];
  if (field.widget === "checkbox") {
    return <label className={styles.switchRow}><span>{field.label}</span><Switch checked={value === true} disabled={disabled} onCheckedChange={(checked) => onChange(field.key, checked)} /></label>;
  }
  return <label className={styles.field}><span className={styles.label}>{field.label}{field.required ? " *" : ""}</span>
    {field.widget === "emailSearch" ? <EmailSearchField field={field} value={typeof value === "string" ? value : ""} disabled={disabled} onChange={(next) => onChange(field.key, next)} />
      : field.widget === "textarea" ? <textarea className={styles.textarea} value={typeof value === "string" ? value : ""} disabled={disabled} placeholder={field.placeholder} onChange={(event) => onChange(field.key, event.target.value)} />
        : <input className={styles.input} type={field.widget === "userEmail" ? "email" : "text"} value={field.type === "stringArray" ? (Array.isArray(value) ? value.join(", ") : "") : typeof value === "string" ? value : ""} disabled={disabled} placeholder={field.placeholder ?? (field.type === "stringArray" ? "Separate addresses with commas" : undefined)} onChange={(event) => onChange(field.key, field.type === "stringArray" ? event.target.value.split(",").map((item) => item.trim()).filter(Boolean) : event.target.value)} />}
    {errors[field.key] && <span className={styles.error}>{errors[field.key]}</span>}
  </label>;
}

export function WorkflowRunModal({ definition, onClose }: { definition: WorkflowDefinition; onClose: () => void }) {
  const auth = useStoredAuth();
  const navigate = useNavigate();
  const email = auth?.user && typeof auth.user === "object" && "email" in auth.user && typeof auth.user.email === "string" ? auth.user.email : "";
  const fields = useMemo(() => orderedWorkflowInputFields(definition.inputFields), [definition.inputFields]);
  const [values, setValues] = useState(() => initialWorkflowInputValues(fields, email));
  const errors = useMemo(() => validate(fields, values), [fields, values]);
  const enqueue = useEnqueueWorkflow();
  const submit = (openInNewTab = false) => {
    if (enqueue.isPending || Object.keys(errors).length) return;
    // Reserve the popup during the user gesture. Browsers block opening a tab
    // later from the asynchronous mutation callback.
    const tab = openInNewTab ? window.open("about:blank", "_blank") : null;
    enqueue.mutate({ workflowName: definition.name, idempotencyKey: crypto.randomUUID(), input: buildWorkflowInput(fields, values) }, {
      onSuccess: ({ runId }) => {
        const path = `/workflows/runs/${encodeURIComponent(runId)}`;
        onClose();
        if (tab && !tab.closed) tab.location.href = path;
        else navigate(path);
      },
      onError: () => { if (tab && !tab.closed) tab.close(); },
    });
  };
  const handleSubmitClick = (event: MouseEvent<HTMLButtonElement>) => submit(event.metaKey || event.ctrlKey);
  const handleSubmitAuxClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (event.button !== 1) return;
    event.preventDefault();
    submit(true);
  };
  return (
    <PlatformModal
      title={`Run ${definition.label}`}
      description={definition.description}
      error={enqueue.error?.message || (enqueue.error ? "Failed to dispatch the run." : null)}
      confirmLabel="Dispatch run"
      pendingLabel="Dispatching…"
      confirmDisabled={Object.keys(errors).length > 0}
      pending={enqueue.isPending}
      onClose={onClose}
      onConfirm={handleSubmitClick}
      onConfirmAuxClick={handleSubmitAuxClick}
    >
      {fields.map((field) => <FieldControl key={field.key} field={field} values={values} errors={errors} disabled={enqueue.isPending} onChange={(key, value) => setValues((current) => ({ ...current, [key]: value }))} />)}
    </PlatformModal>
  );
}
